// Third party
import { AsyncResult } from '@/redux';

// Constants and types
import { ReduxState } from '../types';
import Dictionary from '@/types/dictionary';
import ComparisonItem from '@/types/comparison-item';
import Comparison from '@/types/comparison';
import AppRegion from '@/types/app-region';
import Portfolio from '@/types/portfolio';
import BMOGIFScenario from '@/types/bmo-gif/bmo-gif-scenario';
import { COMPARISON_ITEM_TYPES, PortfolioFlag } from '@/utils/constants';
// Utils
import { deepClone } from '@/utils/objects';
import {
  benchmarkToComparisonItem,
  fundToComparisonItem,
  portfolioToComparisonItem,
  shouldUseMonthlyRollingInReturns,
  stockToComparisonItem,
} from '@/utils/comparison';
import { calculateBenchmarkPortfolioStats, comparisonBenchmarkToBenchmark } from '@/utils/comparison/benchmarks';
import { getStockData } from '../stocks/stock-store';
import { getStockBenchmarks } from '../comparison-benchmarks/comparison-benchmarks-store';
import { getFundData } from '../funds/fund-store';
import { getFundBenchmarks } from '../benchmarks/benchmark-store';
import { getPortfolioById } from '../portfolios';
import { getPortfolioHoldingsData } from '../edit-portfolio';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';
import { reallocateAndRecalculatePortfolio } from '@/utils/portfolio/calculate';
import { getBenchmarkPortfolio } from '../comparison-benchmarks/benchmark-portfolios';
import { getBMOPortfolioHoldingsData, getBenchmarksData } from '../common/actionCreators';
import { Dispatch } from '../../index';
import { selectCurrentTaxRate } from '../taxes/selectors';
import { calculateBMOGifPortfolio } from '@/utils/bmo-gif/calculate';
import { createScenario, portfolioToBmoGifItem } from '@/utils/bmo-gif';
import { recalcBenchmarkItem, recalcPortfolioItem } from './item-calculation';
import { combineProductsIntoGroupItem } from './item-group';
import { SALES_CHARGES } from '@/utils/bmo-gif/constants';
import { getAltData } from '../alts/alts-store';
import altToComparisonItem from '@/utils/comparison/instrument-to-comparison-item/alt-to-comparison-item';
import { COMPARISON_MODIFIED_ITEM_KEYS } from '@/enums/Comparison';

const refreshFundItem =
  (item: ComparisonItem, comparison: Comparison, state: ReduxState, appRegion: AppRegion): AsyncResult<ComparisonItem> =>
  async dispatch => {
    if (!item._id) {
      return item;
    }

    const fund = await dispatch(getFundData(item._id, ['FUND_NOT_FOUND']));
    const benchmarks = await dispatch(getFundBenchmarks(fund));

    // Passing item flags to conversion function to preserve persisted values after Object.assign
    // Passing item holding symbol to conversion function to preserve persisted values after Object.assign
    let newItem = deepClone(item);
    const options = {
      itemFlags: newItem.flags,
      itemSymbol: newItem.holdings[0].symbol,
      comparison,
    };
    newItem = Object.assign(newItem, fundToComparisonItem(fund, appRegion, benchmarks, options));
    return newItem;
  };

const refreshStockItem =
  (item: ComparisonItem, comparison: Comparison, state: ReduxState): AsyncResult<ComparisonItem> =>
  async dispatch => {
    if (!item._id) {
      return item;
    }

    const stock = await dispatch(getStockData(item._id, ['STOCK_NOT_FOUND']));
    const benchmarks = await dispatch(getStockBenchmarks(stock));

    // Passing item flags to conversion function to preserve persisted values after Object.assign
    let newItem = deepClone(item);
    const options = {
      itemFlags: newItem.flags,
      comparison,
    };

    const appRegion: AppRegion = state.auth.user != null ? state.auth.user.appConfig.appRegion : (AppRegion.CAN as AppRegion);

    newItem = Object.assign(newItem, stockToComparisonItem(stock, benchmarks.map(comparisonBenchmarkToBenchmark), options, appRegion));
    return newItem;
  };

const refreshAltItem =
  (item: ComparisonItem, comparison: Comparison, state: ReduxState): AsyncResult<ComparisonItem> =>
  async dispatch => {
    if (!item._id) {
      return item;
    }

    const alt = await dispatch(getAltData(item._id, ['ALT_NOT_FOUND']));

    // Passing item flags to conversion function to preserve persisted values after Object.assign
    const newItem = deepClone(item);
    const options = {
      itemFlags: newItem.flags,
      comparison,
    };
    return Object.assign(newItem, altToComparisonItem(alt, options));
  };

const refreshPortfolioItem =
  (item: ComparisonItem, comparison: Comparison, state: ReduxState, appRegion: AppRegion): AsyncResult<{ newPortfolio: Portfolio; newItem: ComparisonItem }> =>
  async dispatch => {
    const { user } = state.auth;
    let taxRate = 0;

    const portfolio = await dispatch(getPortfolioById(item._id, item.portfolioType, item.contact, item.enterprise, ['PORTFOLIO_NOT_FOUND'], item.source));

    if (portfolio.contact) {
      taxRate = selectCurrentTaxRate(state, portfolio.contact);
    }

    const holdingsData = await dispatch(getPortfolioHoldingsData(portfolio));
    const validateIncomeStream = user != null && userHasPermission(user, PERMISSIONS.INCOME_STREAM);
    const forcedMonthlyRolling: boolean = comparison != null ? shouldUseMonthlyRollingInReturns(comparison, user) : false;
    const newPortfolio = reallocateAndRecalculatePortfolio(portfolio, holdingsData, forcedMonthlyRolling, taxRate, comparison?.commonInception);

    // Passing item flags to conversion function to preserve persisted values after Object.assign
    const options = {
      taxRate,
      shortHistory: comparison.shortHistory,
      itemFlags: item.flags,
      comparison,
      existingItem: item,
    };
    let newItem = Object.assign(deepClone(item), portfolioToComparisonItem(newPortfolio, appRegion, validateIncomeStream, holdingsData, options));
    newItem = await recalcPortfolioItem(
      dispatch,
      state,
      newItem,
      forcedMonthlyRolling,
      comparison.shortHistory,
      comparison.showBaseCurrencies,
      true,
      comparison?.commonInception,
    );

    return { newPortfolio, newItem };
  };

const refreshBenchmarkItem =
  (item: ComparisonItem, state: ReduxState, comparison: Comparison): AsyncResult<ComparisonItem | null> =>
  async dispatch => {
    const { shortHistory, commonInception } = comparison;
    const benchmarkPortfolio = await dispatch(getBenchmarkPortfolio(item, ['BENCHMARK_PORTFOLIO_NOT_FOUND']));
    const benchmarkData = await dispatch(getBenchmarksData(benchmarkPortfolio));

    // Having this here feels off, but with how unlikely this scenario is it's
    // not worth refactoring `getBenchmarkPortfolio` and `getBenchmarksData`.
    if (benchmarkPortfolio.holdings.some(holding => !benchmarkData.some(benchmark => holding.id === benchmark._id))) {
      return null;
    }

    const forcedMonthlyRolling: boolean = shouldUseMonthlyRollingInReturns(comparison, state.auth.user);
    const newBenchmarkPortfolio = await calculateBenchmarkPortfolioStats(
      dispatch,
      benchmarkPortfolio,
      benchmarkData,
      forcedMonthlyRolling,
      shortHistory,
      commonInception,
    );
    let newItem = deepClone(item);
    newItem = Object.assign(newItem, benchmarkToComparisonItem(newBenchmarkPortfolio, benchmarkData, commonInception));

    for (let j = 0; j < newItem.holdings.length; j++) {
      const holding = newItem.holdings[j];
      const holdingData = benchmarkData.find(d => d._id === holding.id);
      holding.rolling = holdingData ? holdingData.performance.rolling : { weekly: [], monthly: [] };
      holding.riskFreeId = holdingData ? holdingData.riskFreeId : undefined;
    }

    if (newItem.holdings.length > 1 && comparison.showBaseCurrencies) {
      newItem = await recalcBenchmarkItem(dispatch, newItem, forcedMonthlyRolling, comparison, shortHistory);
    }

    return newItem;
  };

const refreshGroupItem =
  (item: ComparisonItem, comparison: Comparison, newPortfolios: Dictionary<Portfolio>, appRegion: AppRegion): AsyncResult<ComparisonItem> =>
  async (dispatch, getState) => {
    if (!item.groupItems) {
      return item;
    }
    const state = getState();
    const newItem = await dispatch(combineProductsIntoGroupItem(item.groupItems, comparison, newPortfolios, state, appRegion));

    newItem.color = item.color;
    newItem.name = item.name;
    newItem._id = item._id;

    return newItem;
  };

export const checkForPlanRemoved = (comparison: Comparison, itemId: string, data: ComparisonItem) => {
  const originalItem = comparison.items.find(i => i._id === itemId);
  const planningIdsRemoved = originalItem?.planning?.planId && originalItem?.planning?.planAccountId && !data.planning?.planId && !data.planning?.planAccountId;
  const unableToFetchPlanningData = data.planning?.planId && data.planning?.planAccountId && !data.planning?.planAccount;
  return planningIdsRemoved || unableToFetchPlanningData;
};

export const refreshComparisonData = async (dispatch: Dispatch, state: ReduxState, sourceComparison: Comparison): Promise<Comparison> => {
  const comparison = deepClone(sourceComparison) as Comparison;

  if (!comparison.items.length) {
    return Promise.resolve(comparison);
  }

  const appRegion: AppRegion = state.auth.user != null ? state.auth.user.appConfig.appRegion : (AppRegion.CAN as AppRegion);
  const groupItems = comparison.items.filter(item => item.type === COMPARISON_ITEM_TYPES.GROUP);
  const nonGroupItems = comparison.items.filter(item => item.type !== COMPARISON_ITEM_TYPES.GROUP);
  const newPortfolios: Dictionary<Portfolio> = {};

  const results = await Promise.all<{ id: string; data: ComparisonItem | null }>(
    nonGroupItems.map(async item => {
      try {
        switch (item.type) {
          case COMPARISON_ITEM_TYPES.FUND: {
            return {
              id: item._id,
              data: await dispatch(refreshFundItem(item, comparison, state, appRegion)),
            };
          }
          case COMPARISON_ITEM_TYPES.STOCK: {
            return {
              id: item._id,
              data: await dispatch(refreshStockItem(item, comparison, state)),
            };
          }
          case COMPARISON_ITEM_TYPES.ALT: {
            return {
              id: item._id,
              data: await dispatch(refreshAltItem(item, comparison, state)),
            };
          }
          case COMPARISON_ITEM_TYPES.PORTFOLIO: {
            const { newPortfolio, newItem } = await dispatch(refreshPortfolioItem(item, comparison, state, appRegion));
            newPortfolios[item._id] = newPortfolio;
            return {
              id: item._id,
              data: newItem,
            };
          }
          case COMPARISON_ITEM_TYPES.BENCHMARK: {
            return {
              id: item._id,
              data: await dispatch(refreshBenchmarkItem(item, state, comparison)),
            };
          }
        }
        return Promise.resolve({
          id: item._id,
          data: item,
        });
      } catch (err) {
        if ((err as { status: number }).status === 404) {
          return Promise.resolve({
            id: item._id,
            data: null,
          });
        }

        throw err;
      }
    }),
  );

  let newItems: ComparisonItem[] = [];
  const missingItems: ComparisonItem[] = [];
  const modifiedItems: { item: ComparisonItem; modification: COMPARISON_MODIFIED_ITEM_KEYS }[] = [];

  results.forEach(result => {
    const { id, data } = result;

    if (data != null) {
      newItems.push(data);

      // Check if the item portfolio has been modified
      if (data.type === COMPARISON_ITEM_TYPES.PORTFOLIO && data.flags?.includes(PortfolioFlag.UnallocatedPortfolio)) {
        modifiedItems.push({ item: data, modification: COMPARISON_MODIFIED_ITEM_KEYS.UNALLOCATED_PORTFOLIO });
      }

      const planningChange = checkForPlanRemoved(comparison, id, data);
      if (data.type === COMPARISON_ITEM_TYPES.PORTFOLIO && planningChange) {
        modifiedItems.push({ item: data, modification: COMPARISON_MODIFIED_ITEM_KEYS.PLANNING_REMOVED });
      }

      return;
    }

    const originalItem = comparison.items.find(i => i._id === id);
    if (originalItem) {
      missingItems.push(originalItem);
    }
  });

  if (modifiedItems.length > 0) {
    comparison.modifiedItems = modifiedItems;
  }

  if (missingItems.length > 0) {
    comparison.missingItems = missingItems;
    return comparison;
  }

  // Since groups are composed of other items, these can only be refresh
  // once all underlying items have been refreshed.
  const newGroups = await Promise.all<ComparisonItem>(groupItems.map(item => dispatch(refreshGroupItem(item, comparison, newPortfolios, appRegion))));

  newItems = newItems.concat(newGroups);

  if (comparison.gifItems && comparison.gifItems.length > 0) {
    for (let i = 0; i < comparison.gifItems.length; i++) {
      // Deferred Sales Charge has been banned in Canada since June 1, 2022, so we remove those items to force re-calculation
      if (comparison.gifItems[i].baseScenario.salesCharge.type === SALES_CHARGES.DSC) {
        comparison.gifItems.splice(i);
        continue;
      }

      const portfolioData = await dispatch(getBMOPortfolioHoldingsData(comparison.gifItems[i].portfolio));
      const newPortfolio = calculateBMOGifPortfolio(comparison.gifItems[i].portfolio, portfolioData);
      newPortfolio.province = comparison.contact?.province;
      const newItem = portfolioToBmoGifItem(newPortfolio);

      if (comparison.gifItems[i] && comparison.gifItems[i].scenarios.length) {
        const scenarios: BMOGIFScenario[] = [];
        for (const scenario of comparison.gifItems[i].scenarios) {
          scenarios.push(createScenario(newPortfolio, scenario.withdrawal));
        }
        newItem.scenarios = scenarios;
      }
      comparison.gifItems[i] = newItem;
    }
  }

  comparison.items = comparison.items.map(item => newItems.find(newItem => newItem._id === item._id) as ComparisonItem);

  return comparison;
};
